// Кнопки печати по документам 1С: официальный УПД, договор-счёт, состав документа.
//
// ЧЕМ ЭТА КНОПКА ОТЛИЧАЕТСЯ ОТ components/PrintButton.tsx (инженерный блок).
// Там лист собирается из уже загруженной карточки: `собрать()` — синхронная функция, и
// возраст зеркала известен ДО нажатия, потому что его отдаёт отдельная ручка ядра.
// Здесь ни того, ни другого. В зеркале от реализации есть только шапка: ни строк табличной
// части, ни справочников номенклатуры, ОКЕИ, стран и ГТД в Postgres нет вовсе. Значит лист
// нельзя собрать, не сходив в 1С, — и возраст данных становится известен только вместе с
// ответом. Отсюда три отличия:
//   • сборка асинхронная, и на время сборки кнопка показывает, что она думает: до девяти
//     запросов к 1С через прокси занимают секунды, а молчащая кнопка читается как «сломалось»;
//   • свежесть печатается ПОСЛЕ ответа — и на самом листе, и подписью под кнопкой;
//   • отказ 1С показывается словами (`odataMessage`), а не пустым листом. Пустой бланк УПД
//     страшнее отсутствия бланка: его подпишут.
//
// ПЕЧАТЬ НИЧЕГО НЕ ПИШЕТ. Ни в 1С, ни в Bitrix: у прокси ядра записи нет вовсе — наверх
// идёт одна функция и только GET (core_api.py, `odata_route_get`).
import { useState } from 'react'

import { OdataDown, odataGet, odataMessage } from '../api/odata'
import type { OdataBase, OdataMeta } from '../api/odata'
import { Unauthorized } from '../api/client'
import {
  ДокументНеНайден, возраст1С, листСчёта, листУПД, листУПДпростой, открытьЛист, скачатьДок,
  собратьРеализацию,
} from '../lib/print1c'
import type { СобранныйДокумент, УсловияПечати1С } from '../lib/print1c'

/** Какой лист печатаем. Три формы — три разных обещания перед человеком. */
export type Форма1С = 'упд' | 'счёт' | 'состав'

const ЗАГОЛОВКИ: Record<Форма1С, string> = {
  'упд': 'УПД (форма 1137)',
  'счёт': 'Договор-счёт',
  'состав': 'Состав документа',
}

/** Что печатает каждая форма — подсказкой под кнопкой, до нажатия. */
const ПОЯСНЕНИЯ: Record<Форма1С, string> = {
  'упд': 'официальный бланк по постановлению № 1137, 16 граф',
  'счёт': 'счёт на оплату с офертой и банковскими реквизитами',
  'состав': 'девять колонок «для себя» — не бланк для налоговой',
}

interface Props {
  /**
   * Ref_Key документа реализации в 1С. Именно `ref1c`, а не `ref`: `ref` в React занят
   * под ссылку на DOM-узел, и проп с таким именем до компонента не доедет.
   */
  ref1c: string
  /**
   * База 1С. По умолчанию УТ — потому что зеркало отражает именно её: Ref_Key реализации
   * из `ut_sales` находится в `ut` и НЕ находится в `bp` (проверено живым GET 28.07.2026,
   * база БП отвечает `value: []`). Ставить `bp` имеет смысл только для документа, ref
   * которого взят из самой БП: часть граф бланка (ОКЕИ, ставка, страна, ГТД) доступна
   * только оттуда — см. `СХЕМЫ` в lib/print1c.ts.
   */
  база?: OdataBase
  форма?: Форма1С
  /** Номер документа — на имя файла .doc и на подпись кнопки. */
  номер?: string | null
  /** Готовы ли данные карточки. Пока нет — кнопка есть, но нажимать нечего. */
  готово?: boolean
}

/** Свежесть словами — тем же текстом, что уходит на лист. */
function подписьСвежести(м: OdataMeta | null): string {
  if (!м) return 'свежесть ответа 1С неизвестна'
  if (м.stale) return `1С не ответила, данные из старого кеша (${возраст1С(м.ageSec)})`
  if (м.cache === 'miss') return 'прочитано из 1С только что'
  return `из кеша ядра, возраст ${возраст1С(м.ageSec)}`
}

export function Print1cButton({
  ref1c, база = 'ut', форма = 'упд', номер = null, готово = true,
}: Props) {
  const [занят, setЗанят] = useState(false)
  const [беда, setБеда] = useState('')
  const [свежесть, setСвежесть] = useState<OdataMeta | null>(null)

  /** Собрать документ и превратить его в лист. Отдельно от вывода: .doc и печать делят её. */
  const собрать = async (): Promise<{ html: string; имя: string } | null> => {
    setБеда('')
    setЗанят(true)
    try {
      const собранный: СобранныйДокумент = await собратьРеализацию(
        (путь) => odataGet<Record<string, unknown>>(база, путь),
        ref1c,
        база === 'bp' ? '1c:bp' : '1c:ut',
      )
      setСвежесть(собранный.свежесть)
      const условия: УсловияПечати1С = {
        свежесть: собранный.свежесть,
        чегоНет: собранный.пропуски.несобрано,
      }
      const д = собранный.документ
      const html = форма === 'упд' ? листУПД(д, условия)
        : форма === 'счёт' ? листСчёта(д, условия)
          : листУПДпростой(д, условия)
      const имя = `${ЗАГОЛОВКИ[форма]} ${д.номер ?? номер ?? ''}`.trim()
      return { html, имя }
    } catch (e) {
      // Три разных отказа, и человеку они значат разное. Общее «что-то пошло не так»
      // тут особенно вредно: «документа нет в этой базе» лечится сменой базы, а
      // «1С молчит» — ожиданием.
      if (e instanceof ДокументНеНайден) {
        setБеда(`${e.message}. Скорее всего документ живёт в другой базе 1С: зеркало отражает`
          + ' УТ (склад), а официальный УПД оформляют в БП (бухгалтерия), и Ref_Key у них разные.')
      } else if (e instanceof Unauthorized) {
        setБеда('Сессия истекла — войдите заново и повторите.')
      } else if (e instanceof OdataDown) {
        setБеда(odataMessage(e))
      } else {
        setБеда(e instanceof Error ? e.message : String(e))
      }
      return null
    } finally {
      setЗанят(false)
    }
  }

  const напечатать = async () => {
    const л = await собрать()
    if (!л) return
    if (открытьЛист(л.html) === 'blocked') {
      setБеда('Браузер не дал открыть окно печати. Разрешите всплывающие окна для этого адреса'
        + ' и нажмите ещё раз — лист открывается отдельной страницей.')
    }
  }

  const вWord = async () => {
    const л = await собрать()
    if (!л) return
    if (скачатьДок(л.html, л.имя) === 'нет-браузера') {
      setБеда('Сохранить файл не удалось: браузер не дал создать ссылку на скачивание.')
    }
  }

  return (
    <>
      <div className="row">
        <button type="button" disabled={!готово || занят} onClick={напечатать}>
          {занят ? '⏳ Собираем из 1С…' : `🖨 ${ЗАГОЛОВКИ[форма]}`}
        </button>
        <button type="button" disabled={!готово || занят} onClick={вWord}>
          📄 В Word (.doc)
        </button>
      </div>
      <div className="hint">
        {ПОЯСНЕНИЯ[форма]}
        {'. '}
        {занят
          ? 'идём в 1С: шапка, строки и справочники — до девяти запросов'
          : свежесть
            ? подписьСвежести(свежесть)
            : 'данные читаются из 1С в момент нажатия, из зеркала лист собрать нельзя'}
      </div>
      {беда && <div className="notice">{беда}</div>}
    </>
  )
}
